import FormSelect from 'components/Select/FormSelect';
import { ISelectOption } from 'components/Select/types';
import Text from 'components/Text/Text';
import React, { useContext, useState, useMemo, useEffect, useCallback } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import { UserRoles, ITotalPrice, IItemForGetShortInfoWithStatus } from 'types';
import { useRoleAccess } from 'hooks/useRoleAccess';
import { useArchivedMultiSelect } from 'hooks/useArchivedMultiSelect';
import { Checkbox } from 'antd';
import SearchField from 'components/SearchField/SearchField';
import style from './MultipleSelect.module.scss';

interface IMultipleSelect {
	isDisabledInRestaurants: string[];
	totalPrices: ITotalPrice[];
	externalId: string;
	setDisabledInRestaurants: (value: string[]) => void;
}

const MultipleSelect: React.FC<IMultipleSelect> = ({
	isDisabledInRestaurants,
	totalPrices,
	externalId,
	setDisabledInRestaurants,
}) => {
	const { loggedUser } = useContext<IAppContext>(AppContext);
	const hasAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.TECHNICAL,
	]);
	const [searchString, setSearchString] = useState<string>('');

	const items = useMemo<IItemForGetShortInfoWithStatus[]>(
		() =>
			totalPrices.map((el) => ({
				id: el.restaurantId,
				name: el.restaurantName,
				status: el.restaurantStatus,
			})),
		[totalPrices],
	);

	const { showArchived, toggleShowArchived, visibleItems, options } = useArchivedMultiSelect({
		items,
		entityType: 'restaurants',
		selectedIds: isDisabledInRestaurants,
	});

	const filteredData = useMemo(() => {
		return searchString
			? options.filter((el) => el.name.toLowerCase().includes(searchString.toLowerCase()))
			: options;
	}, [searchString, options]);

	const mappedData = useMemo<ISelectOption[]>(() => {
		const accessibleRestaurantIds = loggedUser.restaurants?.map((el) => el.id);
		return filteredData.map((el) => ({
			...el,
			disabled: !hasAccess && !accessibleRestaurantIds?.some((id) => id === el.id),
		}));
	}, [filteredData, loggedUser, hasAccess]);

	// ----- where roles are used, the selection of all is prohibited ----- //
	const handleSelectAllData = useCallback(() => {
		if (hasAccess) {
			if (visibleItems.length && visibleItems.length === isDisabledInRestaurants.length) {
				setDisabledInRestaurants([]);
			} else {
				setDisabledInRestaurants(visibleItems.map((el) => el.id));
			}
		} else {
			const accessibleRestaurantIds = loggedUser.restaurants ? loggedUser.restaurants.map((el) => el.id) : [];
			const visibleIds = visibleItems.map((el) => el.id);
			const filterAccessibleRestaurantIds = accessibleRestaurantIds.filter((el) => visibleIds.includes(el));
			const isCheckedAll = filterAccessibleRestaurantIds.length
				? filterAccessibleRestaurantIds.every((el) => isDisabledInRestaurants.includes(el))
				: false;
			if (isCheckedAll) {
				const filterIsDisabledInRestaurants = isDisabledInRestaurants.filter(
					(el) => !accessibleRestaurantIds.includes(el),
				);
				setDisabledInRestaurants(filterIsDisabledInRestaurants);
			} else {
				const filterFilteredAccessibleRestaurantIds = filterAccessibleRestaurantIds.filter(
					(el) => !isDisabledInRestaurants.includes(el),
				);
				setDisabledInRestaurants([...isDisabledInRestaurants, ...filterFilteredAccessibleRestaurantIds]);
			}
		}
	}, [hasAccess, visibleItems, isDisabledInRestaurants, setDisabledInRestaurants, loggedUser]);
	// ----- where roles are used, the selection of all is prohibited ----- //

	useEffect(() => {
		setSearchString('');
	}, [totalPrices]);

	const renderSelectMenu = useCallback(
		(menu: React.ReactElement) => {
			const getIsCheckedByHasAccess = () => {
				return !!(visibleItems.length && visibleItems.length === isDisabledInRestaurants.length);
			};

			const getIsCheckedByWithoutAccess = () => {
				const accessibleRestaurantIds = loggedUser.restaurants ? loggedUser.restaurants.map((el) => el.id) : [];
				const visibleIds = visibleItems.map((el) => el.id);
				const filterAccessibleRestaurantIds = accessibleRestaurantIds.filter((el) => visibleIds.includes(el));
				return filterAccessibleRestaurantIds.length
					? filterAccessibleRestaurantIds.every((el) => isDisabledInRestaurants.includes(el))
					: false;
			};

			const getDisabledStatus = () => {
				const accessibleRestaurantIds = loggedUser.restaurants ? loggedUser.restaurants.map((el) => el.id) : [];
				const visibleIds = visibleItems.map((el) => el.id);
				return visibleIds.some((el) => accessibleRestaurantIds.includes(el));
			};

			return (
				<>
					<div className={style.selectMenuHeader}>
						<Checkbox
							className={style.checkBox}
							checked={hasAccess ? getIsCheckedByHasAccess() : getIsCheckedByWithoutAccess()}
							disabled={hasAccess ? !visibleItems.length : !getDisabledStatus()}
							onChange={handleSelectAllData}>
							<Text
								translationNameSpace="form"
								translationKey="selectAllRestaurantsLabel"
								asFragment={true}
							/>
						</Checkbox>
						<Checkbox className={style.checkBox} checked={showArchived} onChange={toggleShowArchived}>
							<Text translationNameSpace="modals" translationKey="showArchived" asFragment={true} />
						</Checkbox>
						<SearchField
							onChangeSelectSearchString={setSearchString}
							searchString={searchString}
							className={style.searchField}
						/>
					</div>
					{menu}
				</>
			);
		},
		[
			visibleItems,
			isDisabledInRestaurants,
			loggedUser,
			hasAccess,
			handleSelectAllData,
			setSearchString,
			searchString,
			showArchived,
			toggleShowArchived,
		],
	);

	return (
		<FormSelect
			name="isDisabledInRestaurants"
			label="restaurantsLabel"
			options={mappedData}
			disabled={!totalPrices.length || !!externalId}
			onChange={setDisabledInRestaurants}
			counterValue={isDisabledInRestaurants.length}
			dropdownRender={renderSelectMenu}
			mode="multiple"
		/>
	);
};

export default React.memo(MultipleSelect);
